import { z } from 'zod';

export const MEETING_DEBUG_APPEND_CHANNEL = 'meeting-debug-append';
export const MEETING_DEBUG_READ_CHANNEL = 'meeting-debug-read';

export const MEETING_DEBUG_CAPS = {
  entriesPerMeeting: 200,
  meetings: 50,
  globalEntries: 500,
};

export const MeetingDebugEventSourceSchema = z.enum([
  'contextkit',
  'main',
  'renderer',
]);

export const MeetingDebugEventKindSchema = z.enum([
  'apps_updated',
  'auto_start',
  'start_result',
  'app_bound',
  'segment_event',
  'input_device_changed',
  'stop',
  'interrupt',
  'contextkit_crash',
  'post_processing_state',
  'skipped',
  'contextkit_event',
]);

export const MeetingAutoStartSkipPathSchema = z.enum([
  'app_detection',
  'calendar_reminder',
  'new_event_prompt',
  'segment_prompt',
  'resume',
]);
export type MeetingAutoStartSkipPath = z.infer<
  typeof MeetingAutoStartSkipPathSchema
>;

export const MeetingAutoStartSkipReasonSchema = z.enum([
  'detection_notification_disabled',
  'already_recording',
  'presence_loss_stop_in_progress',
  'presence_loss_notification_pending',
  'debounced',
  'no_apps_detected',
  'meeting_app_gone',
  'reminders_disabled',
  'already_notified',
  'unconfirmed_event',
  'reminder_suppressed',
  'excluded_event_type',
  'cancelled_event',
  'solo_event',
  'declined_event',
  'same_as_recording_event',
  'notification_already_shown',
  'recording_changed',
  'already_in_this_room',
  'already_prompted',
  'nothing_to_resume',
  'meeting_not_found',
]);
export type MeetingAutoStartSkipReason = z.infer<
  typeof MeetingAutoStartSkipReasonSchema
>;

export const buildSkipEntry = (
  path: MeetingAutoStartSkipPath,
  reason: MeetingAutoStartSkipReason,
  fields: Record<string, unknown> = {}
) => ({
  kind: 'skipped' as const,
  summary: `Skipped ${path.replaceAll('_', ' ')}: ${reason.replaceAll('_', ' ')}`,
  fields: { path, reason, ...fields },
});

export const MeetingDebugEventSchema = z.object({
  at: z.number(),
  source: MeetingDebugEventSourceSchema,
  kind: MeetingDebugEventKindSchema,
  meetingId: z.string().optional(),
  detectionId: z.string().optional(),
  autoStartAttemptId: z.string().optional(),
  summary: z.string(),
  fields: z.record(z.string(), z.unknown()),
});
export type MeetingDebugEvent = z.infer<typeof MeetingDebugEventSchema>;

/** A ContextKit meeting-notes row mirrored over IPC: the Axiom event type and its flat metadata */
export const ContextKitMeetingDebugEventSchema = z.object({
  eventType: z.string(),
  fields: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
});
export type ContextKitMeetingDebugEvent = z.infer<
  typeof ContextKitMeetingDebugEventSchema
>;

export const MeetingDebugReadRequestSchema = z.object({
  meetingId: z.string().optional(),
});

export type MeetingDebugReadResponse = {
  /** The requested meeting's entries, or every stored meeting's when no id is given */
  meeting: MeetingDebugEvent[];
  global: MeetingDebugEvent[];
};
